/**
 * THE WHAT-IF ADAPTER — engine grid → slider variants, by coordinate EQUALITY.
 *
 * The engine publishes one engine-bound binding per (slot, parameter value),
 * each carrying its grain as typed `coordinates` (helix-51, 2026-09-24: e.g.
 * `{ period: "2026-Q4", slip_quarters: "1" }`). This groups them into the
 * closed `AskExhibitWhatIf` shape without reading a label or splitting a
 * string: a binding belongs to a variant because its
 * `coordinates[coordinateKey]` EQUALS that value, and to a slot because its
 * `coordinates[slotKey]` equals that slot.
 *
 * Slot order is the order slots first appear in the engine's own binding
 * list, so every variant fills the exhibit's slots identically. An
 * incomplete grid (a missing or duplicated slot in any variant) is refused
 * as `null` — the slider is not offered rather than drawn with a hole.
 */
import type { AskExhibitWhatIf } from './declared-exhibit';

/**
 * The engine's slider metadata plus the two coordinate keys it varies over.
 * `coordinateKey` is named explicitly because the parameter `ref`
 * ("slipQuarters") and the grain key ("slip_quarters") are different
 * identifiers, and deriving one from the other would be string surgery.
 */
export type AskWhatIfParameterDeclaration = Readonly<{
  ref: string;
  coordinateKey: string;
  slotKey: string;
  label: string;
  unit: string;
  values: readonly string[];
}>;

export type AskWhatIfBindingView = Readonly<{
  placeholder: string;
  coordinates?: Readonly<Record<string, string>>;
}>;

/** One engine figure placed on the grid: typed once, so nothing downstream asserts. */
type GridCell = Readonly<{ placeholder: string; value: string; slot: string }>;

export function declareAskWhatIf(
  parameter: AskWhatIfParameterDeclaration,
  bindings: readonly AskWhatIfBindingView[],
): AskExhibitWhatIf | null {
  const cells = gridCells(parameter, bindings);
  if (cells.length === 0) return null;
  const slots = [...new Set(cells.map((cell) => cell.slot))];
  const variants: Array<Readonly<{ value: string; figures: readonly string[] }>> = [];
  for (const value of parameter.values) {
    const variant = variantFor(value, slots, cells);
    if (variant === null) return null;
    variants.push(variant);
  }
  return {
    parameter: {
      ref: parameter.ref, label: parameter.label, unit: parameter.unit, values: parameter.values,
    },
    variants,
  };
}

/**
 * Scope to the DECLARED values before deriving slots: a wider engine grid
 * (e.g. a value-4 binding in an extra quarter) must not add a slot that every
 * declared variant then appears to be missing (Codex HOLD @ 21caafde).
 */
function gridCells(
  parameter: AskWhatIfParameterDeclaration,
  bindings: readonly AskWhatIfBindingView[],
): readonly GridCell[] {
  const declaredValues = new Set(parameter.values);
  const cells: GridCell[] = [];
  for (const binding of bindings) {
    const value = binding.coordinates?.[parameter.coordinateKey];
    const slot = binding.coordinates?.[parameter.slotKey];
    if (value === undefined || slot === undefined || !declaredValues.has(value)) continue;
    cells.push({ placeholder: binding.placeholder, value, slot });
  }
  return cells;
}

function variantFor(
  value: string,
  slots: readonly string[],
  cells: readonly GridCell[],
): Readonly<{ value: string; figures: readonly string[] }> | null {
  const mine = cells.filter((cell) => cell.value === value);
  const figures: string[] = [];
  for (const slot of slots) {
    const matches = mine.filter((cell) => cell.slot === slot);
    if (matches.length !== 1) return null;
    figures.push(matches[0].placeholder);
  }
  return { value, figures };
}
